/**
 * Edición de pedido — mirror del desktop
 * (`apps/desktop/src/renderer/src/pages/OrderEdit.tsx`) con las mismas
 * mecánicas core: grid de productos, cart con qty/discount/tip,
 * PayDialog para cobrar, transitions por estado.
 *
 * Diferencias vs desktop (cierran en iteraciones futuras):
 *   - Sin integración con `inventory` (router no portado al worker). Los
 *     chips de stock / bloqueo por stock quedan afuera por ahora.
 *   - Sin generación de ticket PDF (tickets router no portado).
 *   - Sin permission gating fino — todos los roles ven lo mismo.
 *   - Sin confirm dialog visual para cancelar/vaciar — usamos window.confirm.
 */

import { Button, Card, cn } from '@atreus/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  Check,
  ChevronsRight,
  ImageOff,
  Minus,
  Plus,
  Printer,
  Search,
  Trash,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { resolveImageUrl } from '../components/ImageDropzone.js';
import { PayDialog } from '../features/checkout/PayDialog.js';
import { useFeatures } from '../lib/features.js';
import { formatCOP, parseCOPToCents } from '../lib/format.js';
import {
  orderOriginLabels,
  orderStatusChipClass,
  orderStatusLabels,
} from '../lib/orders.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

import { nextStatus, type OrderStatus } from '@atreus/core';

interface Props {
  orderId: string;
}

const TRANSITION_LABELS: Record<OrderStatus, string> = {
  draft: 'Reabrir',
  placed: 'Confirmar pedido',
  preparing: 'Marcar en preparación',
  ready: 'Marcar listo',
  delivered: 'Marcar entregado',
  paid: 'Marcar pagado',
  cancelled: 'Cancelar',
};

// Tipos mínimos para la UI — derivados del shape que devuelve el worker.
interface OrderHeader {
  id: string;
  origin: 'table' | 'delivery' | 'counter';
  tableLabel: string | null;
  tableNumber: number | null;
  status: OrderStatus;
  subtotalCents: number;
  taxCents: number;
  tipCents: number;
  discountCents: number;
  totalCents: number;
}

interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  productName: string | null;
  productSku: string | null;
  productImageUrl: string | null;
  qty: number;
  unitPriceCents: number;
  note: string | null;
}

export const OrderEditPage = ({ orderId }: Props) => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const features = useFeatures();
  const permissions = usePermissions();
  const canEditDraft = permissions.canDo('orders.editDraft');
  const canCharge = permissions.canDo('orders.charge');
  const canAdvance = permissions.canDo('orders.advance');
  const canCancel = permissions.canDo('orders.cancel');
  const canDiscount = permissions.canDo('orders.applyDiscount');
  const canEditTip = permissions.canDo('tips.edit');
  const canCreate = permissions.canDo('orders.create');

  const detail = trpc.orders.getById.useQuery({ id: orderId });
  const categories = trpc.categories.list.useQuery();

  const invalidate = () => {
    void utils.orders.getById.invalidate({ id: orderId });
    void utils.orders.list.invalidate();
  };

  const addItem = trpc.orders.addItem.useMutation({ onSuccess: invalidate });
  const updateQty = trpc.orders.updateItemQty.useMutation({
    onSuccess: invalidate,
  });
  const removeItem = trpc.orders.removeItem.useMutation({
    onSuccess: invalidate,
  });
  const clearItems = trpc.orders.clearItems.useMutation({
    onSuccess: invalidate,
  });
  const setDiscount = trpc.orders.setDiscount.useMutation({
    onSuccess: invalidate,
  });
  const setTip = trpc.orders.setTip.useMutation({ onSuccess: invalidate });
  const transition = trpc.orders.transition.useMutation({
    onSuccess: invalidate,
  });
  const cancel = trpc.orders.cancel.useMutation({
    onSuccess: () => {
      invalidate();
      void navigate({ to: '/orders' });
    },
  });

  const [search, setSearch] = useState('');
  // `useDeferredValue` separa el input (urgente) del query server-side.
  // Al tipear rápido el input responde mientras se posterga el refetch.
  const deferredSearch = useDeferredValue(search);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  // Picker de modificadores — se declara acá (antes de cualquier early
  // return) para no violar la Rule of Hooks: si lo movemos debajo del
  // `if (detail.isLoading)` el primer render llama menos hooks que el
  // segundo y React tira #310 "Rendered more hooks…".
  const [pickerProduct, setPickerProduct] = useState<{
    id: string;
    name: string;
    priceCents: number;
  } | null>(null);
  const [checkingModifiersFor, setCheckingModifiersFor] = useState<string | null>(
    null,
  );

  // Productos paginados con cursor. El server filtra por search+categoryId,
  // no cargamos todo el catálogo en memoria. Scroll al fondo → fetchNextPage.
  const productsQueryInput = useMemo(() => {
    const input: {
      activeOnly: boolean;
      limit: number;
      search?: string;
      categoryId?: string;
    } = { activeOnly: true, limit: 60 };
    const s = deferredSearch.trim();
    if (s) input.search = s;
    if (categoryId) input.categoryId = categoryId;
    return input;
  }, [deferredSearch, categoryId]);

  const productsQuery = trpc.products.list.useInfiniteQuery(
    productsQueryInput,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    },
  );

  const productItems = useMemo(
    () => productsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [productsQuery.data],
  );

  if (detail.isLoading) {
    return <div className="p-8 text-fg-muted">Cargando pedido…</div>;
  }
  if (detail.error || !detail.data) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-danger">
          {detail.error?.message ?? 'Pedido no encontrado'}
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/orders">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </Button>
      </div>
    );
  }

  const { order, items } = detail.data as {
    order: OrderHeader;
    items: OrderItem[];
  };
  const editable = order.status === 'draft' && canEditDraft;
  const terminal = order.status === 'paid' || order.status === 'cancelled';
  const kitchenFlow = features.isEnabled('kitchen_flow');
  const tipsEnabled = features.isEnabled('tips');
  const nextTo = nextStatus(order.status, { kitchenFlow });
  const next = nextTo ? { to: nextTo, label: TRANSITION_LABELS[nextTo] } : null;

  // Los filtros ya se aplican server-side via `productsQueryInput`.
  const filteredProducts = productItems;

  // `cancel` puede fallar si el pedido tiene pagos (hay que anularlos antes).
  const addOrUpdateError =
    addItem.error?.message ??
    updateQty.error?.message ??
    cancel.error?.message ??
    null;
  const payingStep = next?.to === 'paid';

  const handleCancel = (): void => {
    if (!window.confirm('¿Cancelar pedido? Esta acción no se puede deshacer.'))
      return;
    cancel.mutate({ orderId: order.id });
  };

  const handleClear = (): void => {
    if (!window.confirm('¿Vaciar carrito? Se quitarán todos los items.'))
      return;
    clearItems.mutate({ orderId: order.id });
  };

  // Picker de modificadores — al clickear un producto, chequeamos si
  // tiene modifiers configurados. Si no hay → agregamos directo (rápido
  // para el cajero). Si hay → abrimos dialog para elegir. El state se
  // declara arriba con el resto de hooks (Rule of Hooks).
  const handleAddProduct = async (p: {
    id: string;
    name: string;
    priceCents: number;
  }): Promise<void> => {
    setCheckingModifiersFor(p.id);
    try {
      const mods = await utils.modifiers.listForProduct.fetch({
        productId: p.id,
      });
      if (!mods || mods.length === 0) {
        addItem.mutate({ orderId: order.id, productId: p.id, qty: 1 });
      } else {
        setPickerProduct(p);
      }
    } finally {
      setCheckingModifiersFor(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <OrderHeaderBar
        order={order}
        onAdvance={
          next
            ? payingStep
              ? canCharge
                ? () => setPayOpen(true)
                : undefined
              : canAdvance
                ? () => transition.mutate({ orderId: order.id, to: next.to })
                : undefined
            : undefined
        }
        advanceLabel={payingStep ? 'Cobrar' : next?.label}
        advanceIcon={
          payingStep ? 'wallet' : order.status === 'draft' ? 'check' : 'chevrons'
        }
        advanceDisabled={
          transition.isPending ||
          (order.status === 'draft' && items.length === 0)
        }
        onCancel={!terminal && canCancel ? handleCancel : undefined}
        cancelDisabled={cancel.isPending}
      />

      {payOpen ? (
        <PayDialog orderId={order.id} onClose={() => setPayOpen(false)} />
      ) : null}

      <div className="flex flex-1 overflow-hidden">
        {/* Catálogo */}
        <section className="flex flex-1 flex-col overflow-hidden border-r border-border">
          <div className="flex items-center gap-3 border-b border-border bg-surface px-6 py-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
              <input
                type="search"
                placeholder="Buscar producto…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!editable}
                className="w-full rounded-md border border-border bg-surface px-9 py-2 text-sm outline-none placeholder:text-fg-subtle focus:border-fg disabled:opacity-50"
              />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto border-b border-border bg-surface px-6 py-2">
            <CategoryChip
              label="Todas"
              selected={categoryId === null}
              onClick={() => setCategoryId(null)}
            />
            {(categories.data ?? []).map((c) => (
              <CategoryChip
                key={c.id}
                label={c.name}
                selected={categoryId === c.id}
                onClick={() => setCategoryId(c.id)}
              />
            ))}
          </div>

          {addOrUpdateError ? (
            <div className="mx-6 mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {addOrUpdateError}
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto p-6">
            {!editable ? (
              <Card className="mx-auto max-w-md p-8 text-center text-sm text-fg-muted">
                Este pedido está en estado{' '}
                <span className="font-bold">
                  {orderStatusLabels[order.status]}
                </span>
                . Para agregar productos crea un pedido nuevo.
                {canCreate ? (
                  <Button asChild size="lg" className="mt-4 w-full">
                    <Link to="/orders/new">
                      <Plus className="size-4" />
                      <span>Nuevo pedido</span>
                    </Link>
                  </Button>
                ) : null}
              </Card>
            ) : productsQuery.isLoading ? (
              <div className="text-fg-muted">Cargando productos…</div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-fg-muted">
                Sin productos para estos filtros.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {filteredProducts.map((p) => (
                    <ProductTile
                      key={p.id}
                      product={p}
                      onAdd={() => void handleAddProduct(p)}
                      disabled={addItem.isPending || checkingModifiersFor === p.id}
                    />
                  ))}
                </div>
                <InfiniteScrollSentinel
                  hasNextPage={productsQuery.hasNextPage}
                  isFetchingNextPage={productsQuery.isFetchingNextPage}
                  fetchNextPage={productsQuery.fetchNextPage}
                />
              </>
            )}
          </div>
        </section>

        {/* Cart */}
        <aside className="flex w-[360px] shrink-0 flex-col bg-surface-muted">
          <CartPanel
            order={order}
            items={items}
            editable={editable}
            tipsEnabled={tipsEnabled && canEditTip}
            discountEnabled={canDiscount}
            busy={
              updateQty.isPending ||
              removeItem.isPending ||
              clearItems.isPending ||
              setDiscount.isPending ||
              setTip.isPending
            }
            onUpdateQty={(itemId, qty) => updateQty.mutate({ itemId, qty })}
            onRemove={(itemId) => removeItem.mutate({ itemId })}
            onClear={handleClear}
            onSetDiscount={(discountCents) =>
              setDiscount.mutate({ orderId: order.id, discountCents })
            }
            onSetTip={(tipCents) =>
              setTip.mutate({ orderId: order.id, tipCents })
            }
          />
        </aside>
      </div>
      {pickerProduct ? (
        <ModifierPickerDialog
          product={pickerProduct}
          onClose={() => setPickerProduct(null)}
          onConfirm={(modifiers) => {
            addItem.mutate({
              orderId: order.id,
              productId: pickerProduct.id,
              qty: 1,
              ...(modifiers.length > 0 ? { modifiers } : {}),
            });
            setPickerProduct(null);
          }}
        />
      ) : null}
    </div>
  );
};

// ---------- Header ----------

interface HeaderProps {
  order: OrderHeader;
  onAdvance: (() => void) | undefined;
  advanceLabel: string | undefined;
  advanceIcon?: 'check' | 'chevrons' | 'wallet';
  advanceDisabled: boolean;
  onCancel: (() => void) | undefined;
  cancelDisabled: boolean;
}

const OrderHeaderBar = ({
  order,
  onAdvance,
  advanceLabel,
  advanceIcon = 'chevrons',
  advanceDisabled,
  onCancel,
  cancelDisabled,
}: HeaderProps) => (
  <header className="flex items-center justify-between gap-4 border-b border-border bg-surface px-6 py-4">
    <div className="flex items-center gap-4">
      <Button asChild variant="ghost" size="sm">
        <Link to="/orders">
          <ArrowLeft className="size-4" /> Pedidos
        </Link>
      </Button>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-fg-muted">
            {orderOriginLabels[order.origin]}
            {order.origin === 'table' &&
            (order.tableLabel || order.tableNumber != null)
              ? ` · Mesa ${order.tableLabel ?? order.tableNumber}`
              : ''}
          </span>
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
              orderStatusChipClass(order.status),
            )}
          >
            {orderStatusLabels[order.status]}
          </span>
        </div>
        <div className="mt-0.5 font-mono text-sm text-fg-subtle">
          #{order.id.slice(-6).toUpperCase()}
        </div>
      </div>
    </div>

    <div className="flex items-center gap-2">
      {onCancel ? (
        <Button
          variant="outline"
          size="md"
          onClick={onCancel}
          disabled={cancelDisabled}
        >
          <Ban className="size-4" /> Cancelar
        </Button>
      ) : null}
      {order.status !== 'draft' && order.status !== 'cancelled' ? (
        <TicketButton orderId={order.id} />
      ) : null}
      {onAdvance && advanceLabel ? (
        <Button size="lg" onClick={onAdvance} disabled={advanceDisabled}>
          {advanceIcon === 'check' ? (
            <Check className="size-4" />
          ) : advanceIcon === 'wallet' ? (
            <Wallet className="size-4" />
          ) : (
            <ChevronsRight className="size-4" />
          )}
          {advanceLabel}
        </Button>
      ) : null}
    </div>
  </header>
);

// ---------- Ticket button ----------

/**
 * Abre la vista imprimible del recibo en nueva pestaña. La pestaña
 * auto-dispara `window.print()` al cargar — el usuario confirma en el
 * diálogo del browser (impresora térmica para ticket 80mm o PDF).
 */
const TicketButton = ({ orderId }: { orderId: string }) => (
  <Button asChild variant="outline" size="md">
    <Link
      to="/reports/order/$orderId"
      params={{ orderId }}
      target="_blank"
      title="Imprimir recibo del pedido"
    >
      <Printer className="size-4" /> Recibo
    </Link>
  </Button>
);

// ---------- Product tile ----------

interface TileProduct {
  id: string;
  name: string;
  priceCents: number;
  imageUrl: string | null;
}

const ProductTile = ({
  product,
  onAdd,
  disabled,
}: {
  product: TileProduct;
  onAdd: () => void;
  disabled: boolean;
}) => {
  const resolved = resolveImageUrl(product.imageUrl);
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={disabled}
      className="group flex flex-col overflow-hidden rounded-md border border-border bg-surface text-left transition-colors hover:border-fg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border"
    >
      <div className="aspect-square w-full bg-surface-muted">
        {resolved ? (
          <img
            src={resolved}
            alt={product.name}
            className="size-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-fg-subtle">
            <ImageOff className="size-6" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <div className="line-clamp-2 text-sm font-semibold tracking-tight">
          {product.name}
        </div>
        <div className="mt-auto pt-2">
          <span className="text-base font-black tracking-tight">
            {formatCOP(product.priceCents)}
          </span>
        </div>
      </div>
    </button>
  );
};

const CategoryChip = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors',
      selected
        ? 'border-fg bg-fg text-fg-inverse'
        : 'border-border bg-surface text-fg-muted hover:text-fg',
    )}
  >
    {label}
  </button>
);

// ---------- Cart ----------

interface CartProps {
  order: OrderHeader;
  items: OrderItem[];
  editable: boolean;
  tipsEnabled: boolean;
  discountEnabled: boolean;
  busy: boolean;
  onUpdateQty: (itemId: string, qty: number) => void;
  onRemove: (itemId: string) => void;
  onClear: () => void;
  onSetDiscount: (cents: number) => void;
  onSetTip: (cents: number) => void;
}

const CartPanel = ({
  order,
  items,
  editable,
  tipsEnabled,
  discountEnabled,
  busy,
  onUpdateQty,
  onRemove,
  onClear,
  onSetDiscount,
  onSetTip,
}: CartProps) => (
  <>
    <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
        Carrito · {items.length} item{items.length === 1 ? '' : 's'}
      </span>
      {editable && items.length > 0 ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={busy}
          aria-label="Vaciar carrito"
        >
          <Trash className="size-3.5" /> Vaciar
        </Button>
      ) : null}
    </div>

    <div className="flex-1 overflow-y-auto">
      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-fg-muted">
          {editable ? 'Agrega productos desde la grilla.' : 'Pedido sin items.'}
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((item) => (
            <CartLine
              key={item.id}
              item={item}
              editable={editable}
              busy={busy}
              onUpdateQty={onUpdateQty}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </div>

    <div className="border-t border-border bg-surface">
      <div className="space-y-2 px-4 py-3 text-sm">
        <Row label="Total productos" value={formatCOP(order.subtotalCents)} />
        <EditableMoneyRow
          label="Descuento"
          cents={order.discountCents}
          disabled={!editable || busy || !discountEnabled}
          onChange={onSetDiscount}
        />
        {order.taxCents > 0 ? (
          <>
            <Row
              label="Base sin IVA"
              value={formatCOP(
                order.subtotalCents - order.discountCents - order.taxCents,
              )}
              muted
            />
            <Row label="IVA" value={formatCOP(order.taxCents)} muted />
          </>
        ) : null}
        {tipsEnabled ? (
          <EditableMoneyRow
            label="Propina"
            cents={order.tipCents}
            disabled={
              order.status === 'paid' || order.status === 'cancelled' || busy
            }
            onChange={onSetTip}
          />
        ) : null}
      </div>
      <div className="flex items-center justify-between border-t border-border px-4 py-4">
        <span className="text-xs font-bold uppercase tracking-wider text-fg-muted">
          Total
        </span>
        <span className="text-3xl font-black tracking-tight">
          {formatCOP(order.totalCents)}
        </span>
      </div>
    </div>
  </>
);

const CartLine = ({
  item,
  editable,
  busy,
  onUpdateQty,
  onRemove,
}: {
  item: OrderItem;
  editable: boolean;
  busy: boolean;
  onUpdateQty: (itemId: string, qty: number) => void;
  onRemove: (itemId: string) => void;
}) => {
  const total = item.unitPriceCents * item.qty;
  return (
    <li className="flex items-start gap-3 p-4">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold tracking-tight">
          {item.productName ?? '(producto)'}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-fg-subtle">
          {item.productSku ?? ''}
        </div>
        <div className="mt-1 text-xs text-fg-muted">
          {item.qty} × {formatCOP(item.unitPriceCents)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="text-sm font-bold tracking-tight">
          {formatCOP(total)}
        </div>
        {editable ? (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUpdateQty(item.id, Math.max(0, item.qty - 1))}
              disabled={busy}
              aria-label="Disminuir"
            >
              <Minus className="size-3.5" />
            </Button>
            <span className="w-6 text-center text-sm font-bold tabular-nums">
              {item.qty}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUpdateQty(item.id, item.qty + 1)}
              disabled={busy}
              aria-label="Aumentar"
            >
              <Plus className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(item.id)}
              disabled={busy}
              aria-label="Eliminar"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
};

const Row = ({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) => (
  <div className="flex items-baseline justify-between">
    <span className={cn('text-xs', muted ? 'text-fg-subtle' : 'text-fg-muted')}>
      {label}
    </span>
    <span
      className={cn(
        'tabular-nums',
        muted ? 'text-fg-subtle' : 'font-semibold',
      )}
    >
      {value}
    </span>
  </div>
);

const EditableMoneyRow = ({
  label,
  cents,
  disabled,
  onChange,
}: {
  label: string;
  cents: number;
  disabled: boolean;
  onChange: (cents: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => String(cents / 100));

  const commit = (): void => {
    const parsed = parseCOPToCents(draft);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed !== cents) {
      onChange(parsed);
    }
    setOpen(false);
  };

  const openEditor = (): void => {
    if (disabled) return;
    setDraft(cents === 0 ? '' : String(cents / 100));
    setOpen(true);
  };

  const display = useMemo(() => formatCOP(cents), [cents]);

  if (open) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-fg-muted">{label}</span>
        <input
          autoFocus
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setOpen(false);
          }}
          className="w-28 rounded border border-fg bg-surface px-2 py-1 text-right text-sm font-semibold tabular-nums outline-none"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={openEditor}
      disabled={disabled}
      className={cn(
        'flex w-full items-baseline justify-between rounded px-0 py-0.5 text-left',
        !disabled && 'hover:underline',
      )}
    >
      <span className="text-xs text-fg-muted">{label}</span>
      <span
        className={cn(
          'tabular-nums font-semibold',
          cents === 0 && 'text-fg-subtle',
          !disabled && 'underline-offset-2 hover:underline',
        )}
      >
        {display}
      </span>
    </button>
  );
};

// ---------- InfiniteScrollSentinel ----------

/**
 * Detecta cuando se llega al final de la grilla vía IntersectionObserver
 * y dispara `fetchNextPage`. Ligero y no bloquea el paint — preferible a
 * escuchar `scroll` porque solo avisa cuando el sentinel entra al viewport.
 */
const InfiniteScrollSentinel = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasNextPage || !ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  if (!hasNextPage) return null;
  return (
    <div
      ref={ref}
      className="py-4 text-center text-xs text-fg-muted"
      aria-hidden="true"
    >
      {isFetchingNextPage ? 'Cargando más…' : ''}
    </div>
  );
};

// ---------- ModifierPickerDialog ----------

interface PickedModifier {
  id: string;
  name: string;
  priceDeltaCents: number;
}

/**
 * Dialog que muestra los modificadores configurados para un producto.
 * El cajero toggle checkboxes y confirma. Los obligatorios se
 * pre-seleccionan y no se pueden destildar (asumimos: si es obligatorio,
 * algún valor debe ir — lo más común es 0-delta "sin cambios").
 */
const ModifierPickerDialog = ({
  product,
  onClose,
  onConfirm,
}: {
  product: { id: string; name: string; priceCents: number };
  onClose: () => void;
  onConfirm: (modifiers: PickedModifier[]) => void;
}) => {
  const query = trpc.modifiers.listForProduct.useQuery({
    productId: product.id,
  });
  const modifiersList = (query.data ?? []) as Array<{
    id: string;
    name: string;
    priceDeltaCents: number;
    required: boolean;
  }>;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Pre-selecciona los obligatorios al llegar la lista.
  useEffect(() => {
    if (modifiersList.length === 0) return;
    const req = modifiersList.filter((m) => m.required).map((m) => m.id);
    if (req.length > 0) setSelected(new Set(req));
  }, [query.data]);

  const toggle = (id: string, required: boolean): void => {
    if (required) return; // los obligatorios quedan fijos
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deltaTotal = modifiersList
    .filter((m) => selected.has(m.id))
    .reduce((acc, m) => acc + m.priceDeltaCents, 0);
  const previewPrice = product.priceCents + deltaTotal;

  const handleConfirm = (): void => {
    const picked = modifiersList
      .filter((m) => selected.has(m.id))
      .map((m) => ({
        id: m.id,
        name: m.name,
        priceDeltaCents: m.priceDeltaCents,
      }));
    onConfirm(picked);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="text-lg font-black tracking-tight">
              {product.name}
            </h3>
            <p className="text-xs text-fg-muted">
              Elige las opciones que aplican a este pedido.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
          {query.isLoading ? (
            <p className="text-sm text-fg-muted">Cargando…</p>
          ) : modifiersList.length === 0 ? (
            <p className="text-sm italic text-fg-muted">
              Sin modificadores — pulsa "Agregar" para añadir al pedido.
            </p>
          ) : (
            <ul className="space-y-2">
              {modifiersList.map((m) => {
                const isSelected = selected.has(m.id);
                return (
                  <li key={m.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2',
                        isSelected
                          ? 'border-fg bg-surface-muted'
                          : 'border-border bg-surface hover:border-fg-muted',
                        m.required && 'cursor-default opacity-80',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(m.id, m.required)}
                          disabled={m.required}
                          className="size-4 accent-fg"
                        />
                        <div>
                          <div className="font-medium">{m.name}</div>
                          {m.required ? (
                            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                              Obligatorio
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="font-mono text-sm tabular-nums">
                        {m.priceDeltaCents === 0
                          ? '—'
                          : `${m.priceDeltaCents > 0 ? '+' : ''}${formatCOP(m.priceDeltaCents)}`}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <footer className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
              Precio unidad
            </div>
            <div className="text-lg font-black tracking-tight tabular-nums">
              {formatCOP(previewPrice)}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm}>
              <Plus className="size-4" /> Agregar
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};
